import React, { useEffect, useState } from "react";
import AdminSidebar from "../components/AdminSidebar";
import api from "../lib/api";
import { toast } from "sonner";
import { CreditCard, AlertTriangle, CheckCircle2, Calendar, Users, Plus, Shield } from "lucide-react";

export default function AdminBilling() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [paying, setPaying] = useState(false);
    const [savingCard, setSavingCard] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/admin/billing");
            setData(r.data);
        } catch {
            toast.error("Errore caricamento fatturazione");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const payNow = async () => {
        if (data?.stripe_mode && data.stripe_mode !== "live") {
            toast.error("Stripe non è in modalità LIVE. Checkout bloccato.");
            return;
        }
        setPaying(true);
        try {
            const r = await api.post("/admin/billing/checkout", { origin_url: window.location.origin });
            if (r.data.url) {
                window.location.href = r.data.url;
                return;
            }
            toast.error("URL di pagamento non ricevuto dal server");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore apertura pagamento");
        } finally {
            setPaying(false);
        }
    };

    const saveCard = async () => {
        if (data?.stripe_mode && data.stripe_mode !== "live") {
            toast.error("Stripe non è in modalità LIVE. Checkout bloccato.");
            return;
        }
        setSavingCard(true);
        try {
            const r = await api.post("/admin/billing/save-card", { origin_url: window.location.origin });
            if (r.data.url) {
                window.location.href = r.data.url;
                return;
            }
            toast.error("URL Stripe non ricevuto");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore apertura Stripe");
        } finally {
            setSavingCard(false);
        }
    };

    if (loading) {
        return (
            <div className="flex min-h-screen bg-ink">
                <AdminSidebar />
                <main className="flex-1 p-10 text-gray-500">Caricamento...</main>
            </div>
        );
    }
    if (!data) return null;

    const accrued = data.accrued_commission_cents || 0;
    const threshold = data.billing_threshold_cents || 5000;
    const progress = Math.min(100, Math.round((accrued / threshold) * 100));
    const locked = !!data.billing_locked;
    const savedCard = data.saved_card;

    return (
        <div className="flex min-h-screen bg-ink" data-testid="admin-billing-page">
            <AdminSidebar />
            <main className="flex-1 p-10 space-y-8">
                <div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="text-gold text-xs tracking-[0.4em] uppercase">Amministrazione</div>
                        {data?.stripe_mode === "live" && (
                            <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 px-2 py-0.5 rounded-sm" data-testid="stripe-mode-badge">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span> Stripe LIVE
                            </span>
                        )}
                        {data?.stripe_mode && data.stripe_mode !== "live" && (
                            <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase bg-amber-500/15 border border-amber-500/40 text-amber-300 px-2 py-0.5 rounded-sm" data-testid="stripe-mode-badge">
                                Stripe TEST
                            </span>
                        )}
                    </div>
                    <h1 className="font-serif text-5xl text-white mt-2">Fatturazione</h1>
                    <p className="text-gray-400 text-sm mt-2 max-w-2xl">
                        Commissioni Renova maturate per le prenotazioni ricevute. Collega una carta di credito per velocizzare i pagamenti al raggiungimento della soglia.
                    </p>
                </div>

                {locked && (
                    <div className="bg-red-500/10 border border-red-500/40 rounded-sm p-6 flex items-start gap-4" data-testid="billing-locked-banner">
                        <AlertTriangle size={28} className="text-red-400 flex-shrink-0" strokeWidth={1.5} />
                        <div className="flex-1">
                            <h3 className="font-serif text-2xl text-white">Soglia commissioni raggiunta</h3>
                            <p className="text-sm text-red-200 mt-1">
                                Il tuo ristorante è <strong>temporaneamente non disponibile</strong> alle nuove prenotazioni online finché non salderai il dovuto.
                            </p>
                        </div>
                    </div>
                )}

                {/* Saved card block */}
                <div className="bg-ink-surface border border-white/10 rounded-sm p-6" data-testid="saved-card-section">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0 ${savedCard ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-gold/10 border border-gold/30"}`}>
                                {savedCard ? <Shield size={22} className="text-emerald-400" strokeWidth={1.5} /> : <CreditCard size={22} className="text-gold" strokeWidth={1.5} />}
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.3em] text-gray-400">
                                    {savedCard ? "Carta collegata" : "Nessuna carta collegata"}
                                </div>
                                {savedCard ? (
                                    <div className="text-white font-serif text-xl mt-1" data-testid="saved-card-display">
                                        <span className="uppercase text-sm text-gold mr-2">{savedCard.brand}</span>
                                        •••• {savedCard.last4}
                                        <span className="text-gray-500 text-sm ml-2">scad. {String(savedCard.exp_month).padStart(2, "0")}/{String(savedCard.exp_year).slice(-2)}</span>
                                    </div>
                                ) : (
                                    <div className="text-gray-300 text-sm mt-1 max-w-md">
                                        Inserisci la tua carta per velocizzare il pagamento al raggiungimento della soglia. Nessun addebito immediato.
                                    </div>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={saveCard}
                            disabled={savingCard}
                            className={`inline-flex items-center gap-2 px-5 py-3 rounded-sm font-semibold tracking-widest uppercase text-xs disabled:opacity-50 ${savedCard ? "border border-gold/40 text-gold hover:bg-gold/10" : "bg-gold text-black hover:bg-gold-light"}`}
                            data-testid="save-card-btn">
                            <Plus size={14} /> {savingCard ? "Apertura Stripe..." : (savedCard ? "Aggiorna carta" : "Inserisci la tua carta")}
                        </button>
                    </div>
                </div>

                {/* Accrued card */}
                <div className="grid md:grid-cols-3 gap-5">
                    <div className="md:col-span-2 bg-ink-surface border border-gold/20 rounded-sm p-8" data-testid="accrued-card">
                        <div className="flex items-center gap-3 mb-4">
                            <CreditCard size={20} className="text-gold" strokeWidth={1.5} />
                            <div className="text-[10px] uppercase tracking-[0.3em] text-gold">Commissioni da saldare</div>
                        </div>
                        <div className="flex items-baseline gap-3">
                            <div className="font-serif text-6xl text-white" data-testid="accrued-amount">€ {(accrued / 100).toFixed(2)}</div>
                            <div className="text-gray-500 text-sm">/ soglia € {(threshold / 100).toFixed(2)}</div>
                        </div>
                        <div className="mt-5 h-2 bg-ink rounded-sm overflow-hidden">
                            <div
                                className={`h-full transition-all ${locked ? "bg-red-500" : progress > 75 ? "bg-amber-400" : "bg-gold"}`}
                                style={{ width: `${progress}%` }}
                                data-testid="accrued-progress"
                            />
                        </div>
                        <div className="flex justify-between text-[10px] uppercase tracking-widest text-gray-500 mt-2">
                            <span>{progress}% della soglia</span>
                            <span>Prenotazioni: {data.unsettled_reservations?.length || 0}</span>
                        </div>

                        <button
                            onClick={payNow}
                            disabled={paying || accrued <= 0}
                            className="mt-6 inline-flex items-center gap-2 bg-gold text-black font-semibold px-6 py-3 rounded-sm hover:bg-gold-light tracking-widest uppercase text-sm disabled:opacity-40"
                            data-testid="pay-now-btn">
                            <CreditCard size={16} /> {paying ? "Apertura Stripe..." : `Paga ora € ${(accrued / 100).toFixed(2)}`}
                        </button>
                        <p className="text-[11px] text-gray-500 mt-3">
                            Pagamento sicuro via Stripe. Dopo il saldo il tuo ristorante è immediatamente sbloccato.
                        </p>
                    </div>

                    <div className="bg-ink-surface border border-white/10 rounded-sm p-6 space-y-3">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-gray-400">Regole Renova</div>
                        <div className="text-sm text-gray-300 leading-relaxed">
                            <p className="mb-3">• La commissione è calcolata per coperto (vedi impostazioni) su ogni prenotazione ricevuta.</p>
                            <p className="mb-3">• Il blocco scatta al superamento della soglia e si sblocca appena paghi.</p>
                            <p>• Le prenotazioni annullate dai clienti scontano la commissione maturata.</p>
                        </div>
                    </div>
                </div>

                {/* Unsettled reservations */}
                <div>
                    <h2 className="font-serif text-3xl text-white mb-5">Prenotazioni da conguagliare</h2>
                    {data.unsettled_reservations?.length === 0 ? (
                        <div className="bg-ink-surface border border-white/10 rounded-sm p-10 text-center text-gray-500">
                            Nessuna prenotazione in attesa di conguaglio.
                        </div>
                    ) : (
                        <div className="bg-ink-surface border border-white/10 rounded-sm overflow-hidden" data-testid="unsettled-list">
                            <table className="w-full text-sm">
                                <thead className="bg-ink/50 text-[10px] uppercase tracking-widest text-gray-400">
                                    <tr>
                                        <th className="text-left px-5 py-3">Data</th>
                                        <th className="text-left px-5 py-3">Cliente</th>
                                        <th className="text-left px-5 py-3">Coperti</th>
                                        <th className="text-right px-5 py-3">Commissione</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.unsettled_reservations.map((r) => (
                                        <tr key={r.reservation_id} className="border-t border-white/5" data-testid={`unsettled-${r.reservation_id}`}>
                                            <td className="px-5 py-3 text-white">
                                                <Calendar size={12} className="inline text-gold mr-2" />
                                                {r.date} · {r.slot_time}
                                            </td>
                                            <td className="px-5 py-3 text-gray-300">{r.customer_name}</td>
                                            <td className="px-5 py-3 text-gray-400">
                                                <Users size={12} className="inline text-gold mr-2" />{r.party_size}
                                            </td>
                                            <td className="px-5 py-3 text-right text-gold font-semibold">
                                                € {(r.commission_cents / 100).toFixed(2)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* History */}
                <div>
                    <h2 className="font-serif text-3xl text-white mb-5">Storico fatture</h2>
                    {data.history?.length === 0 ? (
                        <div className="bg-ink-surface border border-white/10 rounded-sm p-10 text-center text-gray-500">
                            Nessuna fattura pagata finora.
                        </div>
                    ) : (
                        <div className="bg-ink-surface border border-white/10 rounded-sm overflow-hidden" data-testid="history-list">
                            <table className="w-full text-sm">
                                <thead className="bg-ink/50 text-[10px] uppercase tracking-widest text-gray-400">
                                    <tr>
                                        <th className="text-left px-5 py-3">Data</th>
                                        <th className="text-left px-5 py-3">N° prenotazioni</th>
                                        <th className="text-left px-5 py-3">Stato</th>
                                        <th className="text-right px-5 py-3">Importo</th>
                                        <th className="text-right px-5 py-3">Rimborsato</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.history.map((inv) => (
                                        <tr key={inv.invoice_id} className="border-t border-white/5" data-testid={`history-${inv.invoice_id}`}>
                                            <td className="px-5 py-3 text-white">{(inv.paid_at || inv.created_at || "").slice(0, 16).replace("T", " ")}</td>
                                            <td className="px-5 py-3 text-gray-400">{inv.reservation_ids?.length || 0}</td>
                                            <td className="px-5 py-3">
                                                {inv.status === "paid" ? (
                                                    <span className="inline-flex items-center gap-1 text-emerald-400 text-xs">
                                                        <CheckCircle2 size={13} /> Pagata
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-gray-400 uppercase">{inv.status}</span>
                                                )}
                                            </td>
                                            <td className="px-5 py-3 text-right text-gold font-semibold">€ {(inv.amount_cents / 100).toFixed(2)}</td>
                                            <td className="px-5 py-3 text-right" data-testid={`refund-${inv.invoice_id}`}>
                                                {inv.refunded_amount_cents > 0 ? (
                                                    <span className="text-sky-300 text-xs">− € {(inv.refunded_amount_cents / 100).toFixed(2)}</span>
                                                ) : (
                                                    <span className="text-gray-600 text-xs">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
